import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { isPaid } from "@/lib/planActions";
import { Link2, Copy, FileDown, Trash2, Lock, RefreshCw } from "lucide-react";

// Public read-only share link + PDF export (Plus/Premium)
export function ShareDialog({ open, onClose }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const paid = isPaid(user);
  const { data } = useQuery({ queryKey: ["share"], queryFn: async () => (await api.get("/meal-plan/share")).data, enabled: open });
  const onError = (e) => {
    if (e.response?.status === 402) toast(t("premium_feature"), { action: { label: t("pricing"), onClick: () => nav("/pricing") } });
    else toast.error(e.response?.data?.detail || "Error");
  };
  const create = useMutation({ mutationFn: () => api.post("/meal-plan/share"), onSuccess: ({ data: d }) => qc.setQueryData(["share"], d), onError });
  const revoke = useMutation({ mutationFn: () => api.delete("/meal-plan/share"), onSuccess: ({ data: d }) => { qc.setQueryData(["share"], d); toast.success(t("link_revoked")); }, onError });
  const url = data?.token ? `${window.location.origin}/share/${data.token}` : null;
  const pdfUrl = data?.token ? `${process.env.REACT_APP_BACKEND_URL}/api/share/${data.token}/pdf?lang=${lang}` : null;
  const busy = create.isPending || revoke.isPending;

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(t("link_copied")); }
    catch { window.prompt(t("copy_link"), url); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("share_title")}</DialogTitle>
          <DialogDescription>{t("share_sub")}</DialogDescription>
        </DialogHeader>
        {!paid ? (
          <div className="rounded-2xl bg-chenia-rosesoft/50 p-4 flex flex-wrap items-center gap-3" data-testid="share-locked">
            <Lock className="h-4 w-4 text-primary" />
            <p className="text-sm flex-1 min-w-40">{t("premium_feature")}</p>
            <Button size="sm" onClick={() => nav("/pricing")} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="share-unlock">{t("unlock")}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold" data-testid="share-status">{url ? t("share_active") : t("share_inactive")}</p>
            {url ? (
              <>
                <div className="flex items-center gap-2 rounded-2xl border border-border px-3 py-2">
                  <Link2 className="h-4 w-4 text-primary shrink-0" />
                  <input readOnly value={url} className="flex-1 min-w-0 bg-transparent text-sm outline-none" data-testid="share-url" onFocus={(e) => e.target.select()} />
                  <button onClick={copy} className="grid place-items-center h-8 w-8 rounded-full hover:bg-secondary" title={t("copy_link")} data-testid="share-copy"><Copy className="h-4 w-4" /></button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button asChild className="rounded-full bg-primary hover:bg-chenia-terracottadark flex-1"><a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="share-pdf"><FileDown className="h-4 w-4 mr-1" /> {t("download_pdf")}</a></Button>
                  <Button variant="ghost" onClick={() => revoke.mutate()} disabled={busy} className="rounded-full text-muted-foreground hover:text-destructive" data-testid="share-revoke"><Trash2 className="h-4 w-4 mr-1" /> {t("revoke_link")}</Button>
                </div>
              </>
            ) : (
              <Button onClick={() => create.mutate()} disabled={busy} className="w-full rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="share-create">
                {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1" /> {t("create_link")}</>}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
